import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Info, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { submitLead, decodeVin, fetchEstimate } from "@/lib/api";
import OfferUpload from "@/components/sections/OfferUpload";
import { siteSource } from "@/lib/skin";
import { toast } from "sonner";

const fmtUSD = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const conditions = [
  { value: "excellent", label: "Excellent", desc: "Like new, no issues, full service records." },
  { value: "good", label: "Good", desc: "Runs well, minor wear, normal mileage." },
  { value: "fair", label: "Fair", desc: "Some cosmetic or mechanical issues, but drives." },
  { value: "poor", label: "Poor", desc: "Major damage, non-runner, or salvage." },
];

const titleStatuses = ["Clean title", "Lien (loan)", "Salvage", "Rebuilt", "Lost"];
const accidentOptions = ["None", "Minor", "Major"];

export default function MultiStepQuoteForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const prefill = location.state?.prefill || {};

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [estimate, setEstimate] = useState({ status: "pending" });

  // Poll for the AI estimate after submission (runs in the background on the server)
  useEffect(() => {
    if (!submitted?.id) return undefined;
    let attempts = 0;
    let cancelled = false;
    const tick = async () => {
      attempts += 1;
      try {
        const e = await fetchEstimate(submitted.id);
        if (cancelled) return;
        if (e.status !== "pending") { setEstimate(e); return; }
      } catch { /* keep polling */ }
      if (attempts >= 30) { setEstimate({ status: "unavailable" }); return; }
      timer = setTimeout(tick, 3000);
    };
    let timer = setTimeout(tick, 2500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [submitted]);
  const [form, setForm] = useState({
    vin: prefill.vin || "",
    license_plate: prefill.license_plate || "",
    state: prefill.state || "CA",
    year: prefill.year || "",
    make: prefill.make || "",
    model: prefill.model || "",
    trim: prefill.trim || "",
    mileage: "",
    condition: "",
    title_status: "",
    accidents: "",
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    notes: "",
    competitor_offer: null,
  });

  const update = (patch) => setForm((p) => ({ ...p, ...patch }));

  // Auto-decode VIN: on mount (prefilled from Home) and whenever a full 17-char VIN is typed.
  const [vinDecode, setVinDecode] = useState({ status: "idle", label: "" });
  useEffect(() => {
    const vin = form.vin;
    if (!vin || vin.length !== 17) { setVinDecode({ status: "idle", label: "" }); return undefined; }
    let cancelled = false;
    setVinDecode({ status: "loading", label: "" });
    const timer = setTimeout(async () => {
      try {
        const d = await decodeVin(vin);
        if (cancelled) return;
        if (!d.make && !d.model) throw new Error("no data");
        setForm((f) => ({ ...f, year: d.year || f.year, make: d.make || f.make, model: d.model || f.model, trim: d.trim || f.trim }));
        setVinDecode({ status: "ok", label: [d.year, d.make, d.model, d.trim].filter(Boolean).join(" ") });
      } catch {
        if (!cancelled) setVinDecode({ status: "error", label: "" });
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.vin]);

  const steps = ["Vehicle", "Condition", "Contact"];
  const stepNo = Math.min(step + 1, steps.length);
  const pct = step >= steps.length ? 100 : ((step + 1) / steps.length) * 100;

  const validateStep = () => {
    if (step === 0) {
      if (!form.year || !form.make || !form.model || !form.mileage) {
        toast.error("Fill in year, make, model and mileage.");
        return false;
      }
    }
    if (step === 1) {
      if (!form.condition) {
        toast.error("Please select your car’s overall condition.");
        return false;
      }
      if (!form.title_status || !form.accidents) {
        toast.error("Please select title status and accident history.");
        return false;
      }
    }
    if (step === 2) {
      if (!form.name || !form.email || !form.phone || !form.zip_code) {
        toast.error("Please complete your contact details.");
        return false;
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
        toast.error("Enter a valid email address.");
        return false;
      }
    }
    return true;
  };

  const next = async () => {
    if (!validateStep()) return;
    if (step < 2) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const { competitor_offer, ...rest } = form;
        const lead = await submitLead({ ...rest, competitor_offer_file_id: competitor_offer?.fileId || null, source: siteSource() });
        setSubmitted(lead);
        setStep(3);
        toast.success("Request sent. We’ll be in touch soon.");
      } catch (err) {
        toast.error("Couldn’t submit — please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden" data-testid="multi-step-form">
      {/* Progress */}
      <div className="px-6 sm:px-10 pt-8 pb-6 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-blue">{step >= steps.length ? "Done" : `Step ${stepNo}`}</span>
            <span className="text-slate-400">·</span>
            <span className="text-sm font-semibold text-slate-700" data-testid="current-step-label">{step >= steps.length ? "Request received" : steps[step]}</span>
          </div>
          <span className="text-xs text-slate-500 font-medium" data-testid="step-counter">{step >= steps.length ? "Complete" : `${stepNo} of ${steps.length}`}</span>
        </div>
        <Progress value={pct} className="h-2 bg-slate-200" />
      </div>

      <div className="p-6 sm:p-10">
        {step === 0 && (
          <div className="space-y-6 fade-up" data-testid="step-vehicle">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Tell us about your car</h2>
              <p className="text-slate-600 mt-1">A VIN helps us identify your exact vehicle. Year, make, model and mileage are enough to start.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="VIN (optional)" testId="form-vin">
                <Input
                  data-testid="form-vin-input"
                  value={form.vin}
                  onChange={(e) => update({ vin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                  placeholder="17-character VIN"
                  maxLength={17}
                  className="h-12 uppercase font-semibold tracking-wider"
                />
                {vinDecode.status === "loading" && (
                  <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1.5" data-testid="vin-decode-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Looking up your VIN…</p>
                )}
                {vinDecode.status === "ok" && (
                  <p className="text-xs text-emerald-700 font-semibold mt-1.5 flex items-center gap-1.5" data-testid="vin-decode-result"><Check className="w-3.5 h-3.5" /> Found: {vinDecode.label}</p>
                )}
                {vinDecode.status === "error" && (
                  <p className="text-xs text-amber-700 mt-1.5" data-testid="vin-decode-error">We couldn’t decode that VIN — please check it, or fill in the details below.</p>
                )}
              </Field>
              <Field label="License plate (optional)" testId="form-plate">
                <Input
                  data-testid="form-plate-input"
                  value={form.license_plate}
                  onChange={(e) => update({ license_plate: e.target.value.toUpperCase() })}
                  placeholder="e.g. 7ABC123"
                  className="h-12 uppercase font-semibold"
                />
              </Field>
              <Field label="Year *" testId="form-year">
                <Input data-testid="form-year-input" inputMode="numeric" value={form.year} onChange={(e) => update({ year: e.target.value.replace(/[^0-9]/g, "").slice(0, 4) })} placeholder="2018" className="h-12" />
              </Field>
              <Field label="Make *" testId="form-make">
                <Input data-testid="form-make-input" value={form.make} onChange={(e) => update({ make: e.target.value })} placeholder="Toyota" className="h-12" />
              </Field>
              <Field label="Model *" testId="form-model">
                <Input data-testid="form-model-input" value={form.model} onChange={(e) => update({ model: e.target.value })} placeholder="Camry" className="h-12" />
              </Field>
              <Field label="Trim (optional)" testId="form-trim">
                <Input data-testid="form-trim-input" value={form.trim} onChange={(e) => update({ trim: e.target.value })} placeholder="SE / XLE / Sport" className="h-12" />
              </Field>
              <Field label="Mileage *" testId="form-mileage">
                <Input data-testid="form-mileage-input" inputMode="numeric" value={form.mileage} onChange={(e) => update({ mileage: e.target.value.replace(/[^0-9]/g, "") })} placeholder="65,000" className="h-12" />
              </Field>
              <Field label="ZIP code" testId="form-zip-top">
                <Input data-testid="form-zip-top-input" inputMode="numeric" value={form.zip_code} onChange={(e) => update({ zip_code: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })} placeholder="90001" className="h-12" />
              </Field>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-7 fade-up" data-testid="step-condition">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Condition & history</h2>
              <p className="text-slate-600 mt-1">Be honest — it helps us give you an accurate offer and avoids surprises at the inspection.</p>
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900 mb-3 block">Overall condition *</Label>
              <RadioGroup value={form.condition} onValueChange={(v) => update({ condition: v })} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {conditions.map((c) => (
                  <label
                    key={c.value}
                    htmlFor={`cond-${c.value}`}
                    data-testid={`condition-${c.value}`}
                    className={`relative flex items-start gap-3 cursor-pointer rounded-2xl border-2 p-4 transition-all ${
                      form.condition === c.value ? "border-brand-blue bg-blue-50/60" : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <RadioGroupItem id={`cond-${c.value}`} value={c.value} className="mt-1" />
                    <div>
                      <div className="font-semibold text-slate-900">{c.label}</div>
                      <div className="text-sm text-slate-600">{c.desc}</div>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Title status *" testId="form-title">
                <Select value={form.title_status} onValueChange={(v) => update({ title_status: v })}>
                  <SelectTrigger data-testid="form-title-trigger" className="h-12"><SelectValue placeholder="Select title status" /></SelectTrigger>
                  <SelectContent>
                    {titleStatuses.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Accident history *" testId="form-accidents">
                <Select value={form.accidents} onValueChange={(v) => update({ accidents: v })}>
                  <SelectTrigger data-testid="form-accidents-trigger" className="h-12"><SelectValue placeholder="Select accident history" /></SelectTrigger>
                  <SelectContent>
                    {accidentOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Anything else we should know? (optional)" testId="form-notes">
              <Textarea data-testid="form-notes-input" value={form.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="Aftermarket parts, recent service, known issues…" className="min-h-[100px]" />
            </Field>
            <OfferUpload value={form.competitor_offer} onChange={(v) => update({ competitor_offer: v })} />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6 fade-up" data-testid="step-contact">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">How do we reach you?</h2>
              <p className="text-slate-600 mt-1">We will call or text to confirm details and arrange an inspection. No spam, and we never sell your information.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name *" testId="form-name">
                <Input data-testid="form-name-input" value={form.name} onChange={(e) => update({ name: e.target.value })} placeholder="Jane Doe" className="h-12" />
              </Field>
              <Field label="Email *" testId="form-email">
                <Input data-testid="form-email-input" type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} placeholder="you@email.com" className="h-12" />
              </Field>
              <Field label="Phone *" testId="form-phone">
                <Input data-testid="form-phone-input" type="tel" value={form.phone} onChange={(e) => update({ phone: e.target.value })} placeholder="(555) 555-1234" className="h-12" />
              </Field>
              <Field label="ZIP code *" testId="form-zip">
                <Input data-testid="form-zip-input" inputMode="numeric" value={form.zip_code} onChange={(e) => update({ zip_code: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })} placeholder="90001" className="h-12" />
              </Field>
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-brand-blue" /> Sent over an encrypted connection and stored privately. We never sell your data.</p>
          </div>
        )}

        {step === 3 && submitted && (
          <div className="text-center py-10 fade-up" data-testid="step-success">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-5">
              <Check className="w-8 h-8" />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Request received</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2 tracking-tight" data-testid="success-heading">
              Thanks, {form.name.split(" ")[0]}. We’ve got your {form.year} {form.make} {form.model}.
            </h2>
            {estimate.status === "ready" && estimate.estimate_low && estimate.estimate_high ? (
              <div className="mt-6 mx-auto max-w-md rounded-2xl border border-blue-100 bg-blue-50/60 p-6 fade-up" data-testid="estimate-card">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-blue">Estimated offer range</p>
                <div className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2 tracking-tight" data-testid="estimate-range">
                  {fmtUSD(estimate.estimate_low)} – {fmtUSD(estimate.estimate_high)}
                </div>
                <p className="text-xs text-slate-500 mt-2">AI-generated estimate based on publicly available trade-in values for your year, make, model, mileage and condition. Not a purchase offer.</p>
              </div>
            ) : estimate.status === "pending" ? (
              <div className="mt-6 mx-auto max-w-md rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="estimate-loading">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-blue">Estimated offer range</p>
                <div className="flex items-center justify-center gap-2 mt-3 text-slate-700 font-semibold">
                  <Loader2 className="w-5 h-5 animate-spin text-brand-blue" /> Estimating your car’s value…
                </div>
                <p className="text-xs text-slate-500 mt-2">This usually takes 10–30 seconds. You can leave this page — we have your request either way.</p>
              </div>
            ) : (
              <p className="text-slate-600 mt-4 max-w-md mx-auto" data-testid="estimate-pending">We could not generate an instant estimate for this vehicle. We will review it personally and get back to you with a number.</p>
            )}
            <p className="text-slate-600 mt-5 max-w-md mx-auto">
              We will call or text you at {form.phone} to confirm the vehicle and arrange an inspection. Your confirmation reference is <span className="font-mono font-semibold text-slate-800" data-testid="lead-reference">{submitted.id.slice(0, 8).toUpperCase()}</span>.
            </p>
            <div className="mt-5 inline-flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm text-left max-w-md" data-testid="offer-disclaimer">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>This is an offer request, not a purchase offer. Your actual offer is made in writing after we inspect the car. You are not obligated to accept.</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <Button onClick={() => navigate("/")} variant="outline" className="h-12 rounded-xl px-6">Back to home</Button>
              <a href="tel:+16503396211">
                <Button className="h-12 bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold rounded-xl px-6">Call (650) 339-6211</Button>
              </a>
              <a href="sms:+16503396211" data-testid="success-sms-link">
                <Button variant="outline" className="h-12 rounded-xl px-6 font-bold">Text us</Button>
              </a>
            </div>
          </div>
        )}

        {step < 3 && (
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100">
            <Button
              data-testid="form-back"
              variant="ghost"
              onClick={back}
              disabled={step === 0}
              className="h-12 rounded-xl disabled:opacity-40"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button
              data-testid="form-next"
              onClick={next}
              disabled={loading}
              className="h-12 px-8 bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold rounded-xl shadow-lg shadow-blue-500/30"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                : step === 2 ? <>Request my offer <ArrowRight className="w-4 h-4 ml-2" /></>
                : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
